import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as DB from '@/lib/supabaseDb';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/feature/Toast';
import PaymentFormModal from '@/components/feature/PaymentFormModal';
import PromissoryNoteFormModal from '@/components/feature/PromissoryNoteFormModal';
import CustomerEditModal from '@/components/feature/CustomerEditModal';
import EnforcementFormModal from '@/components/feature/EnforcementFormModal';
import DebtAdjustmentModal from '@/components/feature/DebtAdjustmentModal';
import PaymentPromiseFormModal from '@/pages/payment-promises/components/PaymentPromiseFormModal';
import CustomerSummaryReport from './components/CustomerSummaryReport';
import MobileCustomerHeader from './components/MobileCustomerHeader';
import CustomerActionGrid from './components/CustomerActionGrid';
import CustomerSummaryGrid from './components/CustomerSummaryGrid';
import CustomerInfoCard from './components/CustomerInfoCard';
import ResponsiveCustomerTabs from './components/ResponsiveCustomerTabs';
import CustomerOverviewTab from './components/CustomerOverviewTab';
import CustomerSalesTab from './components/CustomerSalesTab';
import CustomerPaymentsTab from './components/CustomerPaymentsTab';
import CustomerDeliveriesTab from './components/CustomerDeliveriesTab';
import CustomerNotesTab from './components/CustomerNotesTab';
import CustomerPaymentPromisesTab from './components/CustomerPaymentPromisesTab';
import * as LoanDB from '@/lib/personalLoansDb';
import { formatTRY } from '@/lib/currencyFormat';
import Badge from '@/components/base/Badge';
import CityDistrictSelect from '@/components/base/CityDistrictSelect';
import { customerStatus } from '@/lib/statusDefs';

type TabKey = 'overview' | 'sales' | 'payments' | 'deliveries' | 'notes' | 'payment-promises' | 'debt-adjustments' | 'personal-loans' | 'billing';

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { role, businessCity, businessDistrict } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showNotePaymentModal, setShowNotePaymentModal] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [focusEditNotes, setFocusEditNotes] = useState(false);
  const [showEnforcementModal, setShowEnforcementModal] = useState(false);
  const [showDebtAdjustmentModal, setShowDebtAdjustmentModal] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [showPaymentPromiseModal, setShowPaymentPromiseModal] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [refreshVer, setRefreshVer] = useState(0);
  const [debtAdjustments, setDebtAdjustments] = useState<any[]>([]);
  const [loadingAdjustments, setLoadingAdjustments] = useState(false);

  const [customer, setCustomer] = useState<any>(null);
  const [sales, setSales] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [promissoryNotes, setPromissoryNotes] = useState<any[]>([]);
  const [activeEnforcement, setActiveEnforcement] = useState<any>(null);
  const [saleItemsMap, setSaleItemsMap] = useState<Record<string, any[]>>({});
  const [installmentsBySale, setInstallmentsBySale] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [finSummary, setFinSummary] = useState<DB.CustomerFinancialSummary | null>(null);
  const [personalLoans, setPersonalLoans] = useState<LoanDB.PersonalLoan[]>([]);
  const [loadingLoans, setLoadingLoans] = useState(false);

  // Billing form state
  const [billingSaving, setBillingSaving] = useState(false);
  const [billingForm, setBillingForm] = useState({
    customer_type: 'individual',
    legal_name: '',
    first_name: '',
    last_name: '',
    tax_number: '',
    national_id: '',
    tax_office: '',
    billing_address: '',
    billing_city: '',
    billing_district: '',
    billing_neighborhood: '',
    billing_country: 'Türkiye',
    billing_postal_code: '',
  });

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [custRes, salesRes, payRes, delRes, notesRes, enfRes, finRes] = await Promise.all([
        DB.getCustomerById(id),
        DB.getSalesByCustomerId(id),
        DB.getPaymentsByCustomerId(id),
        DB.getDeliveriesByCustomerId(id),
        DB.getPromissoryNotesByCustomerId(id),
        DB.getActiveEnforcementCaseByCustomerId(id),
        DB.getCustomerFinancialSummary(id),
      ]);

      if (custRes.error) { setError(custRes.error); setLoading(false); return; }
      setCustomer(custRes.data);
      if (finRes.data) setFinSummary(finRes.data);
      
      const salesData = (salesRes.data || []).sort((a, b) => (b.sale_date || '').localeCompare(a.sale_date || ''));
      setSales(salesData);
      setPayments((payRes.data || []).sort((a, b) => (b.payment_date || '').localeCompare(a.payment_date || '')));
      setDeliveries(delRes.data || []);
      setPromissoryNotes(notesRes.data || []);
      setActiveEnforcement(enfRes.data || null);

      // Sync deliveries with sales.delivery_status
      const deliveriesData = delRes.data || [];
      const syncedDeliveries = deliveriesData.map((d: any) => {
        const matchingSale = salesData.find((s: any) => s.id === d.sale_id);
        const saleStatus = matchingSale?.delivery_status;
        if (saleStatus === 'Teslim Edildi' && d.status !== 'Teslim Edildi') {
          return { ...d, status: 'Teslim Edildi' };
        }
        if (saleStatus === 'İptal' && d.status !== 'İptal') {
          return { ...d, status: 'İptal' };
        }
        return d;
      });
      setDeliveries(syncedDeliveries);

      // Fix out-of-sync deliveries in DB (background)
      const outOfSync = deliveriesData.filter((d: any) => {
        const matchingSale = salesData.find((s: any) => s.id === d.sale_id);
        if (!matchingSale) return false;
        return (matchingSale.delivery_status === 'Teslim Edildi' && d.status !== 'Teslim Edildi') ||
               (matchingSale.delivery_status === 'İptal' && d.status !== 'İptal');
      });
      if (outOfSync.length > 0) {
        Promise.all(
          outOfSync.map((d: any) => {
            const matchingSale = salesData.find((s: any) => s.id === d.sale_id);
            return DB.updateDelivery(d.id, { status: matchingSale.delivery_status });
          })
        ).catch(() => {});
      }

      // Load sale_items for all sales in parallel
      if (salesData.length > 0) {
        const saleIds = salesData.map((s: any) => s.id);
        const [itemsResults, instResults] = await Promise.all([
          Promise.all(saleIds.map((saleId: string) => DB.getSaleItems(saleId))),
          DB.getInstallmentsByCustomerId(id),
        ]);

        const itemsMap: Record<string, any[]> = {};
        saleIds.forEach((saleId: string, idx: number) => {
          itemsMap[saleId] = itemsResults[idx]?.data || [];
        });
        setSaleItemsMap(itemsMap);

        const instMap: Record<string, any[]> = {};
        (instResults.data || []).forEach((inst: any) => {
          if (inst.sale_id) {
            if (!instMap[inst.sale_id]) instMap[inst.sale_id] = [];
            instMap[inst.sale_id].push(inst);
          }
        });
        // Sort installments by sequence
        Object.keys(instMap).forEach(key => {
          instMap[key].sort((a, b) => (a.sequence_no || 0) - (b.sequence_no || 0));
        });
        setInstallmentsBySale(instMap);
      }

      setError(null);
    } catch {
      setError('Veri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshVer]);

  useEffect(() => {
    if (customer) {
      setBillingForm({
        customer_type: customer.customer_type || 'individual',
        legal_name: customer.legal_name || '',
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        tax_number: customer.tax_number || '',
        national_id: customer.national_id || '',
        tax_office: customer.tax_office || '',
        billing_address: customer.billing_address || customer.address || '',
        billing_city: customer.billing_city || customer.city || businessCity || '',
        billing_district: customer.billing_district || customer.district || businessDistrict || '',
        billing_neighborhood: customer.billing_neighborhood || customer.neighborhood || '',
        billing_country: customer.billing_country || 'Türkiye',
        billing_postal_code: customer.billing_postal_code || '',
      });
    }
  }, [customer, businessCity, businessDistrict]);

  useEffect(() => {
    if (activeTab === 'debt-adjustments' && id) {
      setLoadingAdjustments(true);
      DB.getDebtAdjustmentsByCustomerId(id)
        .then(res => setDebtAdjustments((res.data || []).sort((a: any, b: any) => (b.created_at || '').localeCompare(a.created_at || ''))))
        .catch(() => setDebtAdjustments([]))
        .finally(() => setLoadingAdjustments(false));
    }
    if (activeTab === 'personal-loans' && id) {
      setLoadingLoans(true);
      LoanDB.getPersonalLoansByCustomerId(id)
        .then(res => setPersonalLoans(res.data || []))
        .catch(() => setPersonalLoans([]))
        .finally(() => setLoadingLoans(false));
    }
  }, [activeTab, id]);

  const handlePaymentCreated = useCallback(() => {
    setRefreshVer(v => v + 1);
  }, []);

  const handleDeleteCustomer = useCallback(async () => {
    if (!id) return;
    setDeleting(true);
    try {
      const result = await DB.deleteCustomer(id);
      if (result.error) {
        showToast(result.error, 'error');
      } else {
        showToast('Müşteri başarıyla silindi.', 'success');
        navigate('/customers');
      }
    } catch {
      showToast('Silme sırasında beklenmedik bir hata oluştu.', 'error');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [id, navigate, showToast]);

  const canEnforce = customer && (customer.total_debt_amount || 0) > 0 && !activeEnforcement;

  // Lock body scroll when more actions sheet is open (mobile)
  useEffect(() => {
    if (showMoreActions) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showMoreActions]);

  // ---- Loading State ----
  if (loading) {
    return (
      <div className="max-w-full overflow-hidden">
        {/* Mobile skeleton */}
        <div className="lg:hidden space-y-4 px-1">
          <div className="flex items-center gap-3 animate-pulse">
            <div className="w-9 h-9 rounded-lg bg-background-200 flex-shrink-0" />
            <div className="flex-1">
              <div className="h-5 bg-background-200 rounded-lg w-40 mb-2" />
              <div className="h-3 bg-background-200 rounded w-24" />
            </div>
            <div className="w-9 h-9 rounded-lg bg-background-200 flex-shrink-0" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="h-12 bg-background-200 rounded-xl" />
            <div className="h-12 bg-background-200 rounded-xl" />
          </div>
          <div className="h-10 bg-background-200 rounded-xl" />
          <div className="grid grid-cols-2 gap-2">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-[88px] bg-background-200 rounded-xl" />
            ))}
          </div>
          <div className="h-32 bg-background-200 rounded-xl" />
          <div className="h-20 bg-background-200 rounded-xl" />
        </div>
        {/* Desktop skeleton */}
        <div className="hidden lg:flex flex-col items-center justify-center h-[60vh]">
          <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin mb-4"></div>
          <h2 className="text-lg font-semibold text-foreground-700">Yükleniyor...</h2>
        </div>
      </div>
    );
  }

  // ---- Error State ----
  if (error) {
    return (
      <div className="max-w-full overflow-hidden">
        <div className="flex flex-col items-center justify-center min-h-[50vh] px-4">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mb-3">
            <i className="ri-error-warning-line text-2xl text-red-400"></i>
          </div>
          <h2 className="text-lg font-semibold text-foreground-700">Yükleme başarısız</h2>
          <p className="text-sm text-foreground-400 mt-1 text-center">{error}</p>
          <button onClick={() => navigate('/customers')} className="btn-outline mt-4 cursor-pointer">Listeye Dön</button>
          <button onClick={() => setRefreshVer(v => v + 1)} className="btn-primary mt-2 cursor-pointer flex items-center gap-2">
            <i className="ri-refresh-line"></i>Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  // ---- Not Found State ----
  if (!customer) {
    return (
      <div className="max-w-full overflow-hidden">
        <div className="flex flex-col items-center justify-center min-h-[50vh] px-4">
          <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-user-unfollow-line text-2xl text-foreground-300"></i>
          </div>
          <h2 className="text-lg font-semibold text-foreground-700">Müşteri bulunamadı</h2>
          <button onClick={() => navigate('/customers')} className="btn-primary mt-4 cursor-pointer">Listeye Dön</button>
        </div>
      </div>
    );
  }

  const tabs: { key: TabKey; label: string; icon: string; count?: number }[] = [
    { key: 'overview', label: 'Genel Bakış', icon: 'ri-information-line' },
    { key: 'sales', label: 'Satışlar', icon: 'ri-shopping-cart-2-line', count: sales.length },
    { key: 'payments', label: 'Ödemeler', icon: 'ri-bank-card-line', count: payments.length },
    { key: 'deliveries', label: 'Teslimatlar', icon: 'ri-truck-line', count: deliveries.length },
    { key: 'notes', label: 'Senetler', icon: 'ri-file-list-3-line', count: promissoryNotes.length },
    { key: 'billing', label: 'Fatura Bilgileri', icon: 'ri-bill-line' },
    { key: 'payment-promises', label: 'Ödeme Sözleri', icon: 'ri-hand-heart-line', count: 0 },
    { key: 'personal-loans', label: 'Elden Borçlar', icon: 'ri-hand-coin-line', count: 0 },
    ...(role === 'admin' ? [{ key: 'debt-adjustments' as TabKey, label: 'Borç Düzeltme', icon: 'ri-scales-3-line', count: 0 }] : []),
  ];

  const summaryItems = [
    { label: 'Toplam Alışveriş', value: formatTRY(finSummary ? finSummary.total_purchase_amount : (customer.total_sales_amount || 0)), color: 'default' as const, icon: 'ri-shopping-cart-2-line' },
    { label: 'Toplam Ödenen', value: formatTRY(finSummary ? finSummary.total_collected : (customer.total_paid_amount || 0)), color: 'emerald' as const, icon: 'ri-bank-card-line' },
    { label: 'Kalan Borç', value: formatTRY(finSummary ? finSummary.total_remaining_debt : (customer.total_debt_amount || 0)), color: (finSummary ? finSummary.total_remaining_debt : (customer.total_debt_amount || 0)) > 0 ? 'red' as const : 'emerald' as const, icon: 'ri-money-dollar-circle-line' },
    { label: 'Son Alışveriş', value: finSummary?.last_purchase_date ? new Date(finSummary.last_purchase_date).toLocaleDateString('tr-TR') : (customer.last_purchase_date ? new Date(customer.last_purchase_date).toLocaleDateString('tr-TR') : '—'), color: 'default' as const, icon: 'ri-calendar-line' },
    { label: 'Son Senet', value: finSummary?.last_note_info ? `${finSummary.last_note_info.note_book_no || '-'} / ${finSummary.last_note_info.note_page_no || '-'} / ${finSummary.last_note_info.note_sequence_no || '-'}` : (() => {
      const openNotes = promissoryNotes.filter((n: any) => ['Bekliyor', 'Kısmi Ödendi', 'Gecikti'].includes(n.status));
      const latest = openNotes[0];
      if (!latest) return '—';
      return `${latest.note_book_no || '-'} / ${latest.note_page_no || '-'} / ${latest.note_sequence_no || '-'}`;
    })(), color: 'default' as const, icon: 'ri-file-list-3-line', onClick: () => {
      const openNotes = promissoryNotes.filter((n: any) => ['Bekliyor', 'Kısmi Ödendi', 'Gecikti'].includes(n.status));
      if (openNotes.length > 0) {
        setActiveTab('notes');
      }
    }},
  ];

  const createdAtStr = customer.created_at ? new Date(customer.created_at).toLocaleDateString('tr-TR') : '';

  const contactFields = [
    {
      label: '1. Telefon',
      value: customer.phone,
      icon: 'ri-phone-line',
      showCall: true,
      showWhatsApp: true,
      phone: customer.phone,
    },
    ...(customer.second_phone ? [{
      label: '2. Telefon',
      value: customer.second_phone,
      icon: 'ri-phone-line',
      showCall: true,
      showWhatsApp: true,
      phone: customer.second_phone,
    }] : []),
    {
      label: 'Adres',
      value: [customer.address, customer.neighborhood, customer.district, customer.city].filter(Boolean).join(', ') || '',
      icon: 'ri-map-pin-line',
    },
    ...(customer.identity_number ? [{
      label: 'TC Kimlik No',
      value: customer.identity_number,
      icon: 'ri-id-card-line',
    }] : []),
  ];

  const pendingDeliveries = (deliveries || []).filter(d => d.status !== 'Teslim Edildi' && d.status !== 'İptal');

  return (
    <div className="max-w-full overflow-x-hidden space-y-4 lg:space-y-5" style={{ paddingBottom: 'calc(96px + env(safe-area-inset-bottom, 0px))' }}>
      {/* Mobile Header */}
      <MobileCustomerHeader
        fullName={customer.full_name}
        status={customer.status}
        customerNo={customer.customer_no || '—'}
        createdAt={createdAtStr}
        onMoreClick={() => setShowMoreActions(true)}
      />

      {/* Desktop Header Row */}
      <div className="hidden lg:flex items-center gap-3">
        <button onClick={() => navigate('/customers')} className="w-9 h-9 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-foreground-600"></i>
        </button>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">{customer.full_name}</h2>
            <Badge badgeClass={customerStatus.badgeClass(customer.status)} icon={customerStatus.icon(customer.status)}>{customer.status}</Badge>
          </div>
          <p className="text-sm text-foreground-400">Müşteri No: {customer.customer_no || '—'} · Kayıt: {createdAtStr}</p>
        </div>
        {/* Desktop actions rendered in CustomerActionGrid */}
        <CustomerActionGrid
          onNewSale={() => navigate(`/sales/new?customerId=${id}`)}
          onAddPayment={() => setShowPaymentModal(true)}
          onAddNote={() => setShowNoteModal(true)}
          onEdit={() => { setFocusEditNotes(false); setShowEditModal(true); }}
          onEnforce={() => setShowEnforcementModal(true)}
          onSubmitToEnforcement={() => setShowEnforcementModal(true)}
          canEnforce={!!canEnforce}
          activeEnforcement={activeEnforcement}
          showMoreSheet={showMoreActions}
          onToggleMore={() => setShowMoreActions(true)}
          onCloseMore={() => setShowMoreActions(false)}
          phone={customer.phone}
          secondPhone={customer.second_phone}
          onDeleteCustomer={() => setShowDeleteConfirm(true)}
          onDebtAdjustment={role === 'admin' ? () => setShowDebtAdjustmentModal(true) : undefined}
          onAddPaymentPromise={() => setShowPaymentPromiseModal(true)}
          onReport={() => setShowReport(true)}
        />
      </div>

      {/* Mobile Actions */}
      <div className="lg:hidden">
        <CustomerActionGrid
          onNewSale={() => navigate(`/sales/new?customerId=${id}`)}
          onAddPayment={() => setShowPaymentModal(true)}
          onAddNote={() => setShowNoteModal(true)}
          onEdit={() => { setFocusEditNotes(false); setShowEditModal(true); }}
          onEnforce={() => setShowEnforcementModal(true)}
          onSubmitToEnforcement={() => setShowEnforcementModal(true)}
          canEnforce={!!canEnforce}
          activeEnforcement={activeEnforcement}
          showMoreSheet={showMoreActions}
          onToggleMore={() => setShowMoreActions(true)}
          onCloseMore={() => setShowMoreActions(false)}
          phone={customer.phone}
          secondPhone={customer.second_phone}
          onDeleteCustomer={() => setShowDeleteConfirm(true)}
          onDebtAdjustment={role === 'admin' ? () => setShowDebtAdjustmentModal(true) : undefined}
          onAddPaymentPromise={() => setShowPaymentPromiseModal(true)}
          onReport={() => setShowReport(true)}
        />
      </div>

      {/* Summary Cards */}
      <CustomerSummaryGrid items={summaryItems} />

      {/* Contact Info */}
      <CustomerInfoCard fields={contactFields} />

      {/* Pending Deliveries Alert */}
      {pendingDeliveries.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 lg:p-4 flex items-center gap-3">
          <span className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-truck-line text-amber-600"></i>
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-800">{pendingDeliveries.length} bekleyen teslimat var</p>
            <p className="text-xs text-amber-600 mt-0.5 truncate">{pendingDeliveries.map(d => d.product_name).filter(Boolean).join(', ')}</p>
          </div>
        </div>
      )}

      {/* Active Enforcement Alert */}
      {activeEnforcement && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 lg:p-4 flex items-center gap-3 flex-wrap">
          <span className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-scales-3-line text-red-600"></i>
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-red-800">Bu müşteri icra takibindedir.</p>
            <div className="flex flex-wrap gap-2 lg:gap-3 mt-1 text-xs text-red-600">
              <span>{activeEnforcement.case_no}</span>
              <span>Takibe Aktarılan: {formatTRY(parseFloat(activeEnforcement.total_debt || 0))}</span>
              <span>Kalan: {formatTRY(parseFloat(activeEnforcement.remaining_debt || 0))}</span>
              <span>Aktarım: {activeEnforcement.enforcement_date ? new Date(activeEnforcement.enforcement_date).toLocaleDateString('tr-TR') : ''}</span>
              <span>Durum: {activeEnforcement.status}</span>
            </div>
          </div>
          <button onClick={() => navigate('/enforcement')} className="btn-outline bg-white text-sm flex items-center gap-1.5 cursor-pointer whitespace-nowrap flex-shrink-0">
            <i className="ri-eye-line"></i>Dosyayı Görüntüle
          </button>
        </div>
      )}

      {/* Tabs */}
      <ResponsiveCustomerTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(key) => setActiveTab(key as TabKey)}
      />

      {/* Tab Content */}
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
        {activeTab === 'overview' && (
          <CustomerOverviewTab
            customer={customer}
            sales={sales}
            payments={payments}
            onEditNotes={() => { setFocusEditNotes(true); setShowEditModal(true); }}
            onNavigate={(path: string) => navigate(path)}
          />
        )}

        {activeTab === 'sales' && <CustomerSalesTab sales={sales} saleItemsMap={saleItemsMap} installmentsBySale={installmentsBySale} deliveries={deliveries} promissoryNotes={promissoryNotes} customerId={id} />}

        {activeTab === 'payments' && <CustomerPaymentsTab payments={payments} />}

        {activeTab === 'deliveries' && <CustomerDeliveriesTab deliveries={deliveries} sales={sales} />}

        {activeTab === 'notes' && (
          <CustomerNotesTab
            notes={promissoryNotes}
            onAddNote={() => setShowNoteModal(true)}
            onPayNote={(noteId: string) => setShowNotePaymentModal(noteId)}
            onViewNoteDetail={() => {}}
          />
        )}

        {activeTab === 'billing' && (
          <div className="p-4 lg:p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-sm font-semibold text-foreground-800">Fatura Bilgileri</h3>
                <p className="text-xs text-foreground-400 mt-0.5">e-Fatura / e-Arşiv için gerekli müşteri bilgileri</p>
              </div>
              <button
                onClick={async () => {
                  if (!id) return;
                  setBillingSaving(true);
                  try {
                    const { error } = await (await import('@/lib/elogoDb')).updateCustomerBillingInfo(id, billingForm);
                    if (error) {
                      showToast(error, 'error');
                    } else {
                      showToast('Fatura bilgileri kaydedildi.', 'success');
                      setRefreshVer(v => v + 1);
                    }
                  } catch {
                    showToast('Kaydedilirken hata oluştu.', 'error');
                  } finally {
                    setBillingSaving(false);
                  }
                }}
                disabled={billingSaving}
                className="btn-primary flex items-center gap-2 px-5 py-2 text-sm cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {billingSaving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    Kaydet
                  </>
                )}
              </button>
            </div>

            {/* Müşteri Türü */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-foreground-600 mb-2">Müşteri Türü</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setBillingForm(f => ({ ...f, customer_type: 'individual' }))}
                  className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    billingForm.customer_type === 'individual'
                      ? 'bg-primary-100 text-primary-700 border border-primary-200'
                      : 'bg-background-100 text-foreground-500 border border-background-200 hover:bg-background-200'
                  }`}
                >
                  <i className="ri-user-line mr-1.5"></i>Gerçek Kişi
                </button>
                <button
                  onClick={() => setBillingForm(f => ({ ...f, customer_type: 'company' }))}
                  className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    billingForm.customer_type === 'company'
                      ? 'bg-primary-100 text-primary-700 border border-primary-200'
                      : 'bg-background-100 text-foreground-500 border border-background-200 hover:bg-background-200'
                  }`}
                >
                  <i className="ri-building-line mr-1.5"></i>Şirket
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {billingForm.customer_type === 'company' ? (
                <>
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-foreground-600 mb-1.5">Firma Ünvanı <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      value={billingForm.legal_name}
                      onChange={e => setBillingForm(f => ({ ...f, legal_name: e.target.value }))}
                      placeholder="Örn: ABC Mobilya Ltd. Şti."
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-600 mb-1.5">Vergi Numarası <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      value={billingForm.tax_number}
                      onChange={e => setBillingForm(f => ({ ...f, tax_number: e.target.value }))}
                      placeholder="10 haneli"
                      maxLength={10}
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-600 mb-1.5">Vergi Dairesi <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      value={billingForm.tax_office}
                      onChange={e => setBillingForm(f => ({ ...f, tax_office: e.target.value }))}
                      placeholder="Örn: Maltepe"
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-foreground-600 mb-1.5">Ad</label>
                    <input
                      type="text"
                      value={billingForm.first_name}
                      onChange={e => setBillingForm(f => ({ ...f, first_name: e.target.value }))}
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-600 mb-1.5">Soyad</label>
                    <input
                      type="text"
                      value={billingForm.last_name}
                      onChange={e => setBillingForm(f => ({ ...f, last_name: e.target.value }))}
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-600 mb-1.5">TC Kimlik No</label>
                    <input
                      type="text"
                      value={billingForm.national_id}
                      onChange={e => setBillingForm(f => ({ ...f, national_id: e.target.value }))}
                      placeholder="11 haneli"
                      maxLength={11}
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-600 mb-1.5">Vergi No (varsa)</label>
                    <input
                      type="text"
                      value={billingForm.tax_number}
                      onChange={e => setBillingForm(f => ({ ...f, tax_number: e.target.value }))}
                      placeholder="10 haneli"
                      maxLength={10}
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Fatura Adresi */}
            <div className="mt-5 pt-5 border-t border-background-100">
              <h4 className="text-sm font-medium text-foreground-700 mb-3">Fatura Adresi</h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-foreground-600 mb-1.5">Adres</label>
                  <textarea
                    value={billingForm.billing_address}
                    onChange={e => setBillingForm(f => ({ ...f, billing_address: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700 resize-none"
                  />
                </div>
                <div className="lg:col-span-2">
                  <CityDistrictSelect
                    city={billingForm.billing_city}
                    district={billingForm.billing_district}
                    neighborhood={billingForm.billing_neighborhood}
                    onCityChange={(city) => setBillingForm(f => ({ ...f, billing_city: city }))}
                    onDistrictChange={(district) => setBillingForm(f => ({ ...f, billing_district: district }))}
                    onNeighborhoodChange={(neighborhood) => setBillingForm(f => ({ ...f, billing_neighborhood: neighborhood }))}
                    selectClassName="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700 cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-600 mb-1.5">Ülke</label>
                  <input
                    type="text"
                    value={billingForm.billing_country}
                    onChange={e => setBillingForm(f => ({ ...f, billing_country: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-600 mb-1.5">Posta Kodu</label>
                  <input
                    type="text"
                    value={billingForm.billing_postal_code}
                    onChange={e => setBillingForm(f => ({ ...f, billing_postal_code: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700"
                  />
                </div>
              </div>
            </div>

            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-600 flex items-start gap-1.5">
                <i className="ri-information-line mt-0.5 shrink-0"></i>
                <span>Fatura bilgileri tamamlanmadan e-Fatura / e-Arşiv gönderimi yapılamaz. Şirket için vergi numarası ve vergi dairesi, gerçek kişi için TCKN veya vergi numarası zorunludur.</span>
              </p>
            </div>
          </div>
        )}

        {activeTab === 'payment-promises' && (
          <CustomerPaymentPromisesTab
            customerId={id!}
            refreshVer={refreshVer}
          />
        )}

        {activeTab === 'personal-loans' && (
          <div className="p-4 lg:p-6">
            {loadingLoans ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></div>
              </div>
            ) : personalLoans.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-12 h-12 rounded-2xl bg-background-100 flex items-center justify-center mb-3">
                  <i className="ri-hand-coin-line text-xl text-foreground-300"></i>
                </div>
                <h4 className="text-sm font-medium text-foreground-600">Elden borç kaydı yok</h4>
                <p className="text-xs text-foreground-400 mt-1">Bu müşteriye verilmiş elden borç bulunmamaktadır.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="text-left py-3 px-3 text-xs font-medium text-foreground-400 uppercase tracking-wider">Borç No</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-foreground-400 uppercase tracking-wider">Verilen</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-foreground-400 uppercase tracking-wider">Geri Alınan</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-foreground-400 uppercase tracking-wider">Kalan</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-foreground-400 uppercase tracking-wider">Tarih</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-foreground-400 uppercase tracking-wider">Vade</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-foreground-400 uppercase tracking-wider">Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {personalLoans.map((loan: any) => (
                      <tr key={loan.id} className="border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer" onClick={() => navigate(`/personal-loans/${loan.id}`)}>
                        <td className="py-3 px-3 whitespace-nowrap font-medium text-primary-600">{loan.loan_no}</td>
                        <td className="py-3 px-3 whitespace-nowrap">{formatTRY(loan.principal_amount)}</td>
                        <td className="py-3 px-3 whitespace-nowrap text-emerald-600">{formatTRY(loan.paid_amount)}</td>
                        <td className="py-3 px-3 whitespace-nowrap font-bold text-amber-600">{formatTRY(loan.remaining_amount)}</td>
                        <td className="py-3 px-3 whitespace-nowrap text-foreground-500 text-xs">{loan.loan_date}</td>
                        <td className="py-3 px-3 whitespace-nowrap text-xs">{loan.due_date || '—'}</td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            loan.status === 'Ödendi' ? 'bg-emerald-100 text-emerald-800' :
                            loan.status === 'Gecikti' ? 'bg-red-100 text-red-800' :
                            loan.status === 'Kısmi Ödendi' ? 'bg-sky-100 text-sky-800' :
                            loan.status === 'İptal' ? 'bg-foreground-100 text-foreground-500' :
                            'bg-amber-100 text-amber-800'
                          }`}>{loan.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'debt-adjustments' && (
          <div className="p-4 lg:p-6">
            {loadingAdjustments ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></div>
              </div>
            ) : debtAdjustments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-12 h-12 rounded-2xl bg-background-100 flex items-center justify-center mb-3">
                  <i className="ri-scales-3-line text-xl text-foreground-300"></i>
                </div>
                <h4 className="text-sm font-medium text-foreground-600">Henüz borç düzeltme kaydı yok</h4>
                <p className="text-xs text-foreground-400 mt-1">Borç düzeltme işlemleri burada listelenir.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="text-left py-3 px-3 text-xs font-medium text-foreground-400 uppercase tracking-wider">Tarih</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-foreground-400 uppercase tracking-wider">Eski Borç</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-foreground-400 uppercase tracking-wider">Yeni Borç</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-foreground-400 uppercase tracking-wider">Fark</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-foreground-400 uppercase tracking-wider">Neden</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-foreground-400 uppercase tracking-wider">İşlemi Yapan</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-foreground-400 uppercase tracking-wider">Bağlantı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debtAdjustments.map((adj: any) => (
                      <tr key={adj.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                        <td className="py-3 px-3 whitespace-nowrap text-foreground-700">
                          {adj.adjustment_date ? new Date(adj.adjustment_date).toLocaleDateString('tr-TR') : '—'}
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap text-foreground-600">{formatTRY(adj.old_total_debt)}</td>
                        <td className="py-3 px-3 whitespace-nowrap text-foreground-800 font-medium">{formatTRY(adj.new_total_debt)}</td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                            (adj.difference || 0) < 0 ? 'bg-emerald-50 text-emerald-700' : (adj.difference || 0) > 0 ? 'bg-red-50 text-red-700' : 'bg-background-100 text-foreground-500'
                          }`}>
                            {(adj.difference || 0) < 0 ? <i className="ri-arrow-down-line text-[10px]"></i> : (adj.difference || 0) > 0 ? <i className="ri-arrow-up-line text-[10px]"></i> : null}
                            {formatTRY(Math.abs(adj.difference || 0))}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-foreground-700 max-w-[160px] truncate" title={adj.reason}>{adj.reason}</td>
                        <td className="py-3 px-3 whitespace-nowrap text-foreground-600">{adj.processed_by_name || '—'}</td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          {adj.linked_type === 'sale' ? (
                            <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                              <i className="ri-shopping-cart-2-line text-[10px]"></i>Satış
                            </span>
                          ) : adj.linked_type === 'note' ? (
                            <span className="inline-flex items-center gap-1 text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">
                              <i className="ri-file-list-3-line text-[10px]"></i>Senet
                            </span>
                          ) : (
                            <span className="text-xs text-foreground-400">Doğrudan</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <PaymentFormModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        mode="customer"
        customerId={id}
        onPaymentCreated={handlePaymentCreated}
      />

      <PromissoryNoteFormModal
        isOpen={showNoteModal}
        onClose={() => { setShowNoteModal(false); setRefreshVer(v => v + 1); }}
        preSelectedCustomerId={id}
      />

      <PaymentFormModal
        open={!!showNotePaymentModal}
        onClose={() => { setShowNotePaymentModal(null); setRefreshVer(v => v + 1); }}
        mode="note"
        noteId={showNotePaymentModal || ''}
        customerId={id}
        onPaymentCreated={() => { setShowNotePaymentModal(null); setRefreshVer(v => v + 1); }}
      />

      <CustomerEditModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setRefreshVer(v => v + 1); }}
        customerId={id!}
        focusNotes={focusEditNotes}
      />

      <EnforcementFormModal
        isOpen={showEnforcementModal}
        onClose={() => { setShowEnforcementModal(false); setRefreshVer(v => v + 1); }}
        preSelectedCustomerId={id}
      />

      <DebtAdjustmentModal
        isOpen={showDebtAdjustmentModal}
        onClose={() => { setShowDebtAdjustmentModal(false); setRefreshVer(v => v + 1); }}
        customerId={id!}
        customerName={customer.full_name}
        currentDebt={customer.total_debt_amount || 0}
      />

      <PaymentPromiseFormModal
        open={showPaymentPromiseModal}
        onClose={() => setShowPaymentPromiseModal(false)}
        onSaved={() => { setShowPaymentPromiseModal(false); setRefreshVer(v => v + 1); }}
        customers={customer ? [customer] : []}
        allSales={sales}
        promissoryNotes={promissoryNotes}
        preSelectedCustomerId={id}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[70] animate-fade-in" onClick={() => setShowDeleteConfirm(false)} />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[71] max-w-md mx-auto bg-background-50 rounded-2xl p-6 shadow-xl animate-slide-up">
            <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-500"></i>
            </div>
            <h3 className="text-lg font-semibold text-foreground-900 text-center">Müşteriyi Sil</h3>
            <p className="text-sm text-foreground-500 text-center mt-2">
              <strong>{customer.full_name}</strong> adlı müşteriyi silmek istediğinize emin misiniz? Bu işlem geri alınamaz ve müşteriye ait tüm kayıtlar silinir.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 btn-outline cursor-pointer"
                disabled={deleting}
              >
                İptal
              </button>
              <button
                onClick={handleDeleteCustomer}
                className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer hover:bg-red-600 active:scale-[0.97] whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Siliniyor...
                  </>
                ) : (
                  'Müşteriyi Sil'
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Customer Summary Report Modal */}
      {showReport && (
        <CustomerSummaryReport
          customerId={id!}
          customerName={customer.full_name}
          customerNo={customer.customer_no || '—'}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
}